"use client";
import useUserEnrollments from "@/components/CourseContent/CourseContext/hooks/useUserEnrollments";
import useUserEntitlements from "@/components/CourseContent/CourseContext/hooks/useUserEntitlements";
import useIsMobile from "@/hooks/useIsMobile";
import isUserEnrolled from "@/lib/course/utils/isUserEnrolled";
import { useParams, useSearchParams } from "next/navigation";
import enrollmentHandler from "../utils/enrollmentHandler";
import { EnrollCourseHookProps } from "../types";
import getEnrollmentClickEventPayload from "../utils/getEnrollmentClickEventPayload";
import useLeadWithPaid from "@/monarch/rules/leadWithPaid/useLeadWithPaid";
import triggerConversionEventUtil from "../utils/triggerConversionEvent";
import { isDateInPast } from "@/lib/date";
import useEmailOptIn from "@/components/CourseContent/CourseContext/hooks/useEmailOptIn";
import { useMonarchTestResult } from "@/monarch/useMonarchTestResult";
import { InternalTrackSelectionTestResult } from "@/monarch/rules/trackSelection/types";
import { ProcessedCourse } from "@/common/course/types";
import { useAuthBaseURL } from "@/hooks/useAuthBaseURL";
import { CommercePath, useBuildCommerceUrl } from "@/hooks/useBuildCommerceUrl";
import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";
import { useAuth } from "@/components/AuthProvider";

function useCourseEnrollment({ courseRun, course }: EnrollCourseHookProps) {
  const authBaseUrl = useAuthBaseURL();
  const { userEnrollments } = useUserEnrollments();
  const { isUserEntitled } = useUserEntitlements();
  const { emailOptIn } = useEmailOptIn();
  const isMobile = useIsMobile();
  const searchParams = useSearchParams();
  const { category, courseSlug } = useParams();
  const { isLoggedIn } = useAuth();

  const { shouldShowModal } = useLogistrationModal();
  const { value: paidLeadValues, isLoading: isPaidLeadLoading } =
    useLeadWithPaid(course);

  const { conversionType, linkDirectToPaymentPage } = paidLeadValues ?? {};

  const isUserEnrolledInRun = isUserEnrolled(
    userEnrollments || [],
    courseRun.key,
  );

  const hasSearchParams = searchParams.toString().length > 0;

  let isFromSearchPage = false;
  let isFromCoursePage = false;

  if (typeof document !== "undefined" && document.referrer) {
    const referrerUrl = document.referrer;
    isFromSearchPage = referrerUrl.includes("/search");
    isFromCoursePage = referrerUrl.includes("/courses");
  }

  const shouldHaveQueryParams =
    (isFromSearchPage && hasSearchParams) || (isFromCoursePage && isLoggedIn);

  const skus = [
    courseRun.seats?.find((seat) => seat.type === "verified")?.sku,
    courseRun.seats?.find((seat) => seat.type === "professional")?.sku,
  ];

  // A purchasable SKU is required for straight-to-checkout.
  // Without one, /lms/payment_page_redirect/ would be called without the required `sku` query param.
  const hasPurchasableSku = Boolean(skus[0] ?? skus[1]);

  const commerceCoordinatorUrl = useBuildCommerceUrl(
    CommercePath.PaymentRedirect,
    {
      sku: skus[0] ?? skus[1],
      course_run_key: courseRun.key,
    },
  );

  const { isEnrollable: isCourseEnrollable } = courseRun;

  const isCourseArchived = courseRun.end ? isDateInPast(courseRun.end) : false;

  const monarchUseNewTrackSelect = useMonarchTestResult<
    InternalTrackSelectionTestResult,
    ProcessedCourse
  >("useInternalTrackSelect", course)?.data?.useInternalTrackSelection;
  const useNewTrackSelect =
    //TODO: remove this once we have the rule updated
    !isCourseArchived &&
    isCourseEnrollable &&
    monarchUseNewTrackSelect === "true";

  const handleEnrollment = async (
    type?: string,
    straightToCheckout?: boolean,
  ) => {
    // noinspection ES6MissingAwait
    enrollmentHandler({
      courseRun,
      course,
      isUserEntitled,
      emailOptIn,
      type: type || conversionType,
      opts: {
        directToPayment:
          (Boolean(linkDirectToPaymentPage) || Boolean(straightToCheckout)) &&
          hasPurchasableSku,
        straightToCheckout: Boolean(straightToCheckout) && hasPurchasableSku,
      },
      commerceCoordinatorUrl,
      shouldHaveQueryParams,
      isUserEnrolledInRun,
      isLoggedIn,
      isMobile,
      searchParams,
      useNewTrackSelect,
      category: category as string,
      courseSlug: courseSlug as string,
      authBaseUrl,
      byPassAuth: shouldShowModal,
    })();
  };

  const getEnrollmentClickEventData = () => {
    return getEnrollmentClickEventPayload({
      courseRun,
      emailOptIn,
      course,
      shouldHaveQueryParams,
      isUserEnrolledInRun,
      isLoggedIn,
      isUserEntitled,
      isMobile,
      searchParams,
      byPassAuth: shouldShowModal,
    });
  };

  const triggerConversionEvent = () => {
    triggerConversionEventUtil({
      customName: conversionType,
      courseRun,
      isUserEntitled,
      course,
    })();
  };

  return {
    getEnrollmentClickEventData,
    handleEnrollment,
    isUserEnrolledInRun,
    isCourseEnrollable,
    isLoggedIn,
    commerceCoordinatorUrl,
    hasPurchasableSku,
    triggerConversionEvent,
    paidLeadValues,
    isPaidLeadLoading,
    isCourseArchived,
    emailOptIn,
  };
}

export default useCourseEnrollment;
